"use client";

import { useEffect, useRef, useState } from "react";
import { captureException } from "@sentry/nextjs";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ApiStatus } from "commonModule/services/common";
import { SearchPropertyByIdType } from "commonModule/services/homepage/propertyLookupAPI";
import SearchIcon from "components/icons-svg/SearchIcon";
import { apiHomePageClient } from "services/apiHomePageClient";
import { customSlugify } from "utils/url";
const LookupByPropertyId = () => {
  const pathname = usePathname();
  const isHomePage = pathname?.startsWith("/");
  const isVacationRentalPage = pathname?.startsWith("/vacation-rentals");
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [propertyId, setPropertyId] = useState("");
  const [searchResults, setSearchResults] = useState<SearchPropertyByIdType | undefined>();
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const pageClassName = `${isVacationRentalPage ? "text-[#222]" : isHomePage ? "text-white absolute md:static top-6 md:top-auto md:left-auto left-[-48px] sm:left-[80px]" : ""}`;

  // Close popover and reset all lookup/search state
  const handleClose = () => {
    setIsOpen(false);
    setPropertyId("");
    setSearchResults(undefined);
    setIsSearching(false);
    setHasSearched(false);
  };

  // Handle outside click and close the lookup popover
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains((event.target as Node))) {
        handleClose();
      }
    };

    // Close lookup popover when Escape key is pressed
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  // Search property by entered property ID
  const handleSearch = async () => {
    const trimmedPropertyId = propertyId.trim();
    if (!trimmedPropertyId) {
      setSearchResults(undefined);
      setHasSearched(false);
      return;
    }
    setIsSearching(true);
    setHasSearched(true);
    await apiHomePageClient.getSearchPropertyById({
      params: {
        propertyId: trimmedPropertyId
      }
    }).then(res => {
      if (res?.data?.apiStatus === ApiStatus.SUCCESS) {
        setSearchResults(res?.data?.data);
      } else {
        setSearchResults(undefined);
      }
      setIsSearching(false);
      return true;
    }).catch(error => {
      captureException(new Error(`Error 'getSearchPropertyById' ${JSON.stringify(error)}`));
      setSearchResults(undefined);
    });
  };

  // Open lookup popover and focus the property ID input
  const handleOpen = () => {
    setIsOpen(prev => !prev);
    if (!isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };
  return <div ref={popoverRef} className='relative' data-sentry-component="LookupByPropertyId" data-sentry-source-file="LookupByPropertyId.tsx">
      {/* Trigger */}
      <button type='button' onClick={handleOpen} aria-expanded={isOpen} aria-haspopup='dialog' className={` mb-0.5 whitespace-nowrap text-[11px] font-medium underline underline-offset-2 lg:text-sm ${pageClassName}`}>
        Lookup by property ID
      </button>

      {isOpen && <div className={`absolute z-[1002] w-[300px] rounded-[10px] border border-[#E5E5E5] bg-white shadow-[0px_8px_24px_rgba(0,0,0,0.15)] xsm:w-[320px] md:top-[calc(100%+13px)] md:w-[386px]
                    ${isVacationRentalPage ? "right-0 top-[30px] " : isHomePage ? "-right-20 top-[50px] sm:right-[-207px] md:right-0  " : ""}`} role='dialog' aria-label='Lookup by property ID'>
          {/* Arrow */}
          <div className='absolute -top-[7px] right-[46px] h-[14px] w-[14px] rotate-45 border-l border-t border-[#E5E5E5] bg-white' aria-hidden='true' />

          <div className='relative'>
            {/* Header */}
            <div className='px-[18px] pb-[12px] pt-[16px]'>
              <h3 className='text-[15px] font-semibold leading-[20px] text-[#273746]'>
                Look up a property by ID
              </h3>
            </div>

            {/* Search */}
            <div className='px-[18px]'>
              <div className='relative'>
                <input ref={inputRef} type='text' value={propertyId} onChange={event => {
              const value = event.target.value.replace(/\s/g, "");
              setPropertyId(value);
              if (!event.target.value.trim()) {
                setSearchResults(undefined);
                setHasSearched(false);
              }
            }} onKeyDown={event => {
              if (event.key === "Enter") {
                event.preventDefault();
                if (!isSearching) {
                  void handleSearch(); // Trigger property search on search icon click
                }
              }
            }} placeholder='Enter property ID' className='h-[36px] w-full rounded-[10px] border border-[#D9D9D9] bg-white px-[14px] pr-[42px] text-[14px] text-[#273746] outline-none placeholder:text-[#8B9298] focus:border-[#AFAFAF]' />

                <button type='button' onClick={() => void handleSearch()} // Trigger property search on search icon click
            disabled={isSearching} aria-label='Search property' className='absolute right-[10px] top-1/2 flex -translate-y-1/2 items-center justify-center disabled:opacity-50'>
                  <SearchIcon className='h-[18px] w-[18px] stroke-[#53616C]' />
                </button>
              </div>
            </div>

            {/* Results */}
            <div className='px-[18px] pb-4'>
              {/* Section title */}
              {!isSearching && searchResults && <h4 className='pb-2 pt-[13px] text-[13px] font-semibold leading-[18px] text-[#273746]'>
                  Search results
                </h4>}

              {/* Loading */}
              {isSearching && <p className='flex h-20 items-center justify-center text-[13px] text-[#69757D]'>
                  Searching...
                </p>}

              {/* No results */}
              {propertyId !== "" && hasSearched && !isSearching && !searchResults && <div className='block min-h-[100px] pt-[30px] text-center'>
                    <p className='text-[13px] text-[#69757D]'>
                      No property found for property ID{" "}
                    </p>
                    <p className='break-all text-[13px] font-semibold text-[#69757D]'>
                      {propertyId}
                    </p>
                  </div>}

              {/* Property list */}
              {!isSearching && searchResults && <div className='overflow-hidden rounded-[4px]'>
                  <div key={searchResults.propertyId} className='flex min-h-[47px] items-center border-b border-[#E9E9E9] px-[12px] last:border-b-0'>
                    {/* Property ID */}
                    <div className='w-[80px] shrink-0'>
                      <span className='text-[12px] font-semibold text-[#34495E]'>
                        {searchResults.propertyId}
                      </span>
                    </div>

                    {/* Property name/location */}
                    <div className='min-w-0 flex-1 pr-[8px]'>
                      <p className='truncate text-[12px] font-medium text-[#34495E]'>
                        {searchResults.propertyName}
                      </p>
                    </div>

                    {/* View */}
                    <Link onClick={() => setIsOpen(false)} target='_blank' href={`/detail/${customSlugify(searchResults?.propertyName || "") || "Whimstay"}/${searchResults?.propertyId}`} className='flex h-[29px] min-w-[56px] shrink-0 items-center justify-center rounded-[10px] border border-[#CDBB9F] px-[12px] text-[12px] font-semibold text-[#765A3A] transition-colors hover:bg-[#F8F4EF]'>
                      View
                    </Link>
                  </div>
                </div>}
            </div>
          </div>
        </div>}
    </div>;
};
export default LookupByPropertyId;